<template>
  <div class="sal-screen">
    <SALHeader
      v-if="currentStep < 5"
      brand="SCRAP ALMACÉN Y LASER"
      :step="currentStep"
      :total-steps="5"
    />

    <main class="sal-content" :class="{ 'sal-content--success': currentStep === 5 }">
      <SALStep1Registro
        v-if="currentStep === 1"
        v-model="form"
        :operadores="operadores"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <SALStep2Produccion
        v-else-if="currentStep === 2"
        v-model="form"
        :productos="productos"
        :colores="colores"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <SALStep3Material
        v-else-if="currentStep === 3"
        v-model="form"
        :codigos-error="codigosError"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <SALStep4Resumen v-else-if="currentStep === 4" :form="form" />

      <SALStep5Confirmacion v-else-if="currentStep === 5" :form="form" />
    </main>

    <SALBottomBar
      v-if="currentStep < 5"
      :show-back="canGoBack"
      :next-label="buttonLabel"
      :next-icon="buttonIcon"
      :loading="isSubmitting"
      @back="goBack"
      @next="handleNext"
    />

    <SALBottomBar
      v-else
      next-label="Nuevo Reporte"
      next-icon="bi-arrow-repeat"
      @next="handleNewRecord"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";

import SALHeader from "./components/SALHeader.vue";
import SALBottomBar from "./components/SALBottomBar.vue";
import SALStep1Registro from "./steps/SALStep1Registro.vue";
import SALStep2Produccion from "./steps/SALStep2Produccion.vue";
import SALStep3Material from "./steps/SALStep3Material.vue";
import SALStep4Resumen from "./steps/SALStep4Resumen.vue";
import SALStep5Confirmacion from "./steps/SALStep5Confirmacion.vue";

import Swal from "sweetalert2";
import { useScrapAlmacenLaserForm } from "./composables/useScrapAlmacenLaserForm";
import { crearReporteScrapAlmacenLaser } from "./services/scrapAlmacenLaserApi";
import {
  getOperadoresPublic,
  getProductosPublic,
  getPigmentosPublic,
  getCodigosDefectoPublic,
} from "@/utils/api_scrap_almacen_laser";

import "./styles/scrap-almacen-laser.css";

const {
  form,
  currentStep,
  canGoBack,
  parseNumber,
  formatKg,
  validateStep,
  buildPayload,
  next,
  back,
  resetForm,
  completeDraft,
} = useScrapAlmacenLaserForm();

const isSubmitting = ref(false);
const allowStepBypass = false; // true = permite navegar aunque los steps reales aún no estén conectados. false = activa validación real por paso.

const errors = ref({
  fecha: false,
  operador: false,
  producto: false,
  color: false,
  aditivo: false,
  cantidad: false,
  codigoError: false,
});

const operadores = ref([]);
const productos = ref([]);
const colores = ref([]);
const codigosError = ref([]);

function mapOperadores(ops = []) {
  return ops.map((op) => ({
    value: op.id,
    label: op.nombre_completo,
  }));
}

function mapProductos(prods = []) {
  return prods.map((p) => ({
    value: p.id,
    label: p.nombre,
  }));
}

function mapColores(cols = []) {
  return cols.map((c) => ({
    value: c.id,
    label: c.nombre,
    hex: c.hexadecimal,
  }));
}

function mapCodigosDefecto(errs = []) {
  return errs.map((e) => ({
    value: e.id,
    codigo: e.codigo,
    label: e.descripcion,
  }));
}

async function cargarCatalogo(nombre, loader, mapper, target, fallback = []) {
  try {
    const data = await loader();
    const mapped = mapper(data);
    target.value = mapped.length > 0 ? mapped : [...fallback];
  } catch (error) {
    console.error(`Error al cargar catálogo ${nombre}:`, error);
    target.value = [...fallback];
  }
}

const cargarCatalogos = async () => {
  await Promise.all([
    cargarCatalogo("operadores", getOperadoresPublic, mapOperadores, operadores),
    cargarCatalogo("productos", getProductosPublic, mapProductos, productos),
    cargarCatalogo("colores", getPigmentosPublic, mapColores, colores),
    cargarCatalogo(
      "códigos de defecto",
      getCodigosDefectoPublic,
      mapCodigosDefecto,
      codigosError
    ),
  ]);
};

onMounted(() => {
  cargarCatalogos();
});

const buttonLabel = computed(() => {
  if (isSubmitting.value) return "Enviando...";
  if (currentStep.value === 4) return "Aceptar y enviar";
  return "Siguiente";
});

const buttonIcon = computed(() => {
  if (currentStep.value === 4) return "bi-send-check";
  return "bi-arrow-right";
});

function goBack() {
  back();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function handleNext() {
  if (!allowStepBypass && !validateCurrentStep()) {
    return;
  }

  if (currentStep.value < 4) {
    next();
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  if (currentStep.value === 4) {
    await submitReport();
  }
}

async function submitReport() {
  try {
    isSubmitting.value = true;

    const payload = buildPayload();
    const [response] = await Promise.all([
      crearReporteScrapAlmacenLaser(payload),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);

    form.folio = response?.folio || generarFolioTemporal();
    form.submittedAt = response?.submittedAt || new Date().toISOString();

    next();
    completeDraft();
  } catch (error) {
    console.error("Error al enviar reporte de scrap almacén y laser:", error);

    Swal.fire({
      icon: "error",
      title: "Error al enviar",
      text: "No se pudo enviar el reporte. Verifica tu conexión e intenta de nuevo.",
    });
  } finally {
    isSubmitting.value = false;
  }
}

function generarFolioTemporal() {
  const year = String(new Date().getFullYear()).slice(-2);
  const random = Math.floor(1000 + Math.random() * 9000);
  return `CSAL-${year}-${random}`;
}

function handleNewRecord() {
  resetForm();
  resetErrors();
}

function resetErrors() {
  Object.keys(errors.value).forEach((key) => {
    errors.value[key] = false;
  });
}

function handleFieldChange({ field }) {
  if (!field) return;
  errors.value[field] = false;
}

function validateCurrentStep() {
  resetErrors();

  if (currentStep.value === 1) {
    errors.value.fecha = !form.fecha;
    errors.value.operador = !form.operador;
  }

  if (currentStep.value === 2) {
    errors.value.producto = !form.producto;
    errors.value.color = !form.color;
    errors.value.aditivo = typeof form.aditivo !== "boolean";
  }

  if (currentStep.value === 3) {
    errors.value.cantidad = form.cantidad === "" || parseNumber(form.cantidad) < 0;
    //codigosError es array ahora
    errors.value.codigoError = !form.codigosError || form.codigosError.length === 0;
  }

  return validateStep(currentStep.value);
}
</script>
